import React, { useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { ReportConfigRow } from '../../../../store/slices/reportingConfigurationSlice';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import { REPORTING_ICON_OPTIONS, REPORTING_PRIMARY_TAG_OPTIONS } from '../../constants/reportingConfiguration';
import styles from './ReportingConfiguration.styles';
import { useTheme } from '../../../../theme/useTheme';

export interface ReportingConfigurationFormState extends Omit<ReportConfigRow, 'id'> {
  id: string | null;
}

interface ReportingConfigurationModalProps {
  visible: boolean;
  formState: ReportingConfigurationFormState;
  kpiInput: string;
  kpiOptions: string[];
  onSetName: (value: string) => void;
  onSetDescription: (value: string) => void;
  onSetLink: (value: string) => void;
  onSetIcon: (value: string) => void;
  onSetPrimaryTag: (value: string) => void;
  onToggleStatus: () => void;
  onToggleSecondaryTag: (value: string) => void;
  onSetKpiInput: (value: string) => void;
  onAddKpiTag: (value: string) => void;
  onRemoveKpiTag: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving?: boolean;
}

const normalize = (value: string): string => value.trim().toLowerCase();

const ReportingConfigurationModal: React.FC<ReportingConfigurationModalProps> = ({
  visible,
  formState,
  kpiInput,
  kpiOptions,
  onSetName,
  onSetDescription,
  onSetLink,
  onSetIcon,
  onSetPrimaryTag,
  onToggleStatus,
  onToggleSecondaryTag,
  onSetKpiInput,
  onAddKpiTag,
  onRemoveKpiTag,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const [isKpiDropdownVisible, setIsKpiDropdownVisible] = useState(false);
  const [isIconDropdownVisible, setIsIconDropdownVisible] = useState(false);

  // Determine if this is an internal redirect report (protected fields)
  const isInternalRedirectReport = formState.reportType === 'INTERNAL_REDIRECT';

  const hasRequiredFields =
    !!formState.name.trim() &&
    !!formState.description.trim() &&
    !!formState.link.trim();

  const filteredKpiOptions = useMemo(() => {
    const query = normalize(kpiInput);
    const selected = new Set(formState.kpiTags.map((tag) => normalize(tag)));

    return kpiOptions
      .filter((tag) => {
        const normalizedTag = normalize(tag);

        if (selected.has(normalizedTag)) {
          return false;
        }

        if (!query) {
          return true;
        }

        return normalizedTag.includes(query);
      })
      .slice(0, 40);
  }, [formState.kpiTags, kpiInput, kpiOptions]);

  const canAddCustomKpi = useMemo(() => {
    const clean = kpiInput.trim();
    if (!clean) {
      return false;
    }

    const existsInOptions = kpiOptions.some((tag) => normalize(tag) === normalize(clean));
    const alreadySelected = formState.kpiTags.some((tag) => normalize(tag) === normalize(clean));

    return !existsInOptions && !alreadySelected;
  }, [formState.kpiTags, kpiInput, kpiOptions]);

  const filteredIconOptions = useMemo(() => {
    const query = normalize(formState.icon);

    if (!query) {
      return REPORTING_ICON_OPTIONS;
    }

    return REPORTING_ICON_OPTIONS.filter((iconName) => normalize(iconName).includes(query));
  }, [formState.icon]);

  const truncatedName = formState.name.length > 30
    ? `${formState.name.substring(0, 30)}....`
    : formState.name;

  return (
    <AdminModal
      visible={visible}
      title={formState.id ? `Edit Report: ${truncatedName}` : 'Add Report'}
      onClose={onClose}
      width={760}
      footer={(
        <>
          <AdminButton label="Cancel" variant="secondary" onPress={onClose} />
          <AdminButton label="Save Changes" variant="primary" onPress={onSave} disabled={!hasRequiredFields || isSaving} loading={isSaving} />
        </>
      )}
    >
      {/* Spinner shows inside Save button via AdminButton `loading` prop */}
      <View style={styles.formGroup}>
        <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>
          Report Name
          <Text style={styles.requiredAsterisk}> *</Text>
        </Text>
        <TextInput
          value={formState.name}
          onChangeText={onSetName}
          editable={!isInternalRedirectReport}
          style={[styles.formInput, isInternalRedirectReport && localStyles.formInputDisabled, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }]}
          placeholder="Enter report name"
          placeholderTextColor={isDark ? colors.text.disabled : '#818181'}
        />
        {isInternalRedirectReport && (
          <Text style={[localStyles.disabledFieldHint, isDark && { color: colors.text.disabled }]}>System-managed field. Editing is not allowed.</Text>
        )}
      </View>

      <View style={styles.formGroup}>
        <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>
          Description
          <Text style={styles.requiredAsterisk}> *</Text>
        </Text>
        <TextInput
          value={formState.description}
          onChangeText={onSetDescription}
          style={[styles.formInput, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }]}
          placeholder="Enter report description"
          placeholderTextColor={isDark ? colors.text.disabled : '#818181'}
        />
      </View>

      <View style={styles.formRow}>
        <View style={[styles.formGroup, styles.flex1]}>
          <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>
            Link
            <Text style={styles.requiredAsterisk}> *</Text>
          </Text>
          <TextInput
            value={formState.link}
            onChangeText={onSetLink}
            editable={!isInternalRedirectReport}
            style={[styles.formInput, isInternalRedirectReport && localStyles.formInputDisabled, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }]}
            placeholder="Enter report link"
            placeholderTextColor={isDark ? colors.text.disabled : '#818181'}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {isInternalRedirectReport && (
            <Text style={[localStyles.disabledFieldHint, isDark && { color: colors.text.disabled }]}>System-managed field. Editing is not allowed.</Text>
          )}
        </View>

        <View style={[styles.formGroup, styles.flex1]}>
          <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>Status</Text>
          <TouchableOpacity
            style={[styles.statusToggle, formState.status === 'Active' && styles.statusToggleActive, isDark && { borderColor: colors.border.default }]}
            onPress={onToggleStatus}
          >
            <View style={[styles.statusSwitchTrack, formState.status === 'Active' && styles.statusSwitchTrackActive]}>
              <View style={[styles.statusSwitchKnob, formState.status === 'Active' && styles.statusSwitchKnobActive]} />
            </View>
            <Text style={[styles.statusToggleText, formState.status === 'Active' && styles.statusToggleTextActive, isDark && !( formState.status === 'Active') && { color: colors.text.secondary }]}>
              {formState.status}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.formRow}>
        <View style={[styles.formGroup, styles.flex1]}>
          <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>Primary Tag</Text>
          <View style={styles.optionsWrap}>
            {REPORTING_PRIMARY_TAG_OPTIONS.map((tag) => {
              const selected = formState.primaryTag === tag;
              return (
                <TouchableOpacity
                  key={tag}
                  style={[styles.optionChip, selected && styles.optionChipActive, isDark && !selected && { borderColor: colors.border.default, backgroundColor: colors.background.elevated }]}
                  onPress={() => onSetPrimaryTag(tag)}
                >
                  <Text style={[styles.optionChipText, selected && styles.optionChipTextActive, isDark && !selected && { color: colors.text.secondary }]}>{tag}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        <View style={[styles.formGroup, styles.flex1]}>
          <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>Secondary Tag(s)</Text>
          <View style={styles.optionsWrap}>
            {REPORTING_PRIMARY_TAG_OPTIONS.map((tag) => {
              const selected = formState.secondaryTags.includes(tag);
              return (
                <TouchableOpacity
                  key={tag}
                  style={[styles.optionChip, selected && styles.optionChipActive, isDark && !selected && { borderColor: colors.border.default, backgroundColor: colors.background.elevated }]}
                  onPress={() => onToggleSecondaryTag(tag)}
                >
                  <Text style={[styles.optionChipText, selected && styles.optionChipTextActive, isDark && !selected && { color: colors.text.secondary }]}>{tag}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>

      <View style={styles.formRow}>
        <View style={[styles.formGroup, styles.flex1]}>
          <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>KPI Tag(s)</Text>
          <View style={styles.kpiInputRow}>
            <TextInput
              value={kpiInput}
              onChangeText={onSetKpiInput}
              style={[styles.formInput, styles.flex1, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }]}
              placeholder="Search KPI tags"
              placeholderTextColor={isDark ? colors.text.disabled : '#818181'}
              onFocus={() => setIsKpiDropdownVisible(true)}
              onBlur={() => {
                setTimeout(() => {
                  setIsKpiDropdownVisible(false);
                }, 120);
              }}
              onSubmitEditing={() => {
                if (filteredKpiOptions.length > 0) {
                  onAddKpiTag(filteredKpiOptions[0]);
                  return;
                }

                onAddKpiTag(kpiInput);
              }}
            />
          </View>

          {isKpiDropdownVisible ? (
            <View style={[styles.kpiDropdownMenu, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              {filteredKpiOptions.length > 0 ? (
                <ScrollView style={styles.kpiDropdownList} keyboardShouldPersistTaps="handled">
                  {filteredKpiOptions.map((tag) => (
                    <TouchableOpacity
                      key={tag}
                      style={[styles.kpiDropdownOption, isDark && { borderBottomColor: colors.border.divider }]}
                      onPress={() => {
                        onAddKpiTag(tag);
                        setIsKpiDropdownVisible(false);
                      }}
                    >
                      <Text style={[styles.kpiDropdownOptionText, isDark && { color: colors.text.primary }]}>{tag}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              ) : (
                <Text style={[styles.kpiDropdownEmptyText, isDark && { color: colors.text.secondary }]}>No KPI tags found</Text>
              )}

              {canAddCustomKpi ? (
                <TouchableOpacity
                  style={[styles.kpiDropdownAddNew, isDark && { borderTopColor: colors.border.divider }]}
                  onPress={() => {
                    onAddKpiTag(kpiInput);
                    setIsKpiDropdownVisible(false);
                  }}
                >
                  <MaterialIcons name="add" size={14} color={isDark ? colors.brand.primary : '#005CBC'} />
                  <Text style={[styles.kpiDropdownAddNewText, isDark && { color: colors.brand.primary }]}>Add "{kpiInput.trim()}"</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          {formState.kpiTags.length > 0 ? (
            <View style={styles.badgeWrap}>
              {formState.kpiTags.map((tag) => (
                <View key={tag} style={[styles.removablePill, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
                  <Text style={[styles.removablePillText, isDark && { color: colors.text.primary }]}>{tag}</Text>
                  <TouchableOpacity onPress={() => onRemoveKpiTag(tag)}>
                    <MaterialIcons name="close" size={14} color={isDark ? colors.text.secondary : '#4B4B4B'} />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        <View style={[styles.formGroup, styles.flex1]}>
          <Text style={[styles.formLabel, isDark && { color: colors.text.secondary }]}>Icon</Text>
          <View style={styles.iconInputRow}>
            <View style={[styles.iconPreviewWrap, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              <MaterialIcons name={formState.icon as any} size={18} color={isDark ? colors.brand.primary : '#005CBC'} />
            </View>
            <TextInput
              value={formState.icon}
              onChangeText={onSetIcon}
              style={[styles.formInput, styles.flex1, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }]}
              placeholder="Search material icon"
              placeholderTextColor={isDark ? colors.text.disabled : '#818181'}
              autoCapitalize="none"
              autoCorrect={false}
              onFocus={() => setIsIconDropdownVisible(true)}
              onBlur={() => {
                setTimeout(() => {
                  setIsIconDropdownVisible(false);
                }, 120);
              }}
              onSubmitEditing={() => {
                if (filteredIconOptions.length > 0) {
                  onSetIcon(filteredIconOptions[0]);
                }
              }}
            />
          </View>

          {isIconDropdownVisible ? (
            <View style={[styles.iconDropdownMenu, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              {filteredIconOptions.length > 0 ? (
                <ScrollView style={styles.iconDropdownList} keyboardShouldPersistTaps="handled">
                  {filteredIconOptions.map((iconName) => {
                    const selected = normalize(formState.icon) === normalize(iconName);
                    return (
                      <TouchableOpacity
                        key={iconName}
                        style={[styles.iconDropdownOption, selected && styles.iconDropdownOptionActive, isDark && { borderBottomColor: colors.border.divider }, isDark && selected && { backgroundColor: '#1A2A3A' }]}
                        onPress={() => {
                          onSetIcon(iconName);
                          setIsIconDropdownVisible(false);
                        }}
                      >
                        <MaterialIcons name={iconName as any} size={18} color={selected ? (isDark ? colors.brand.primary : '#005CBC') : (isDark ? colors.text.secondary : '#4B4B4B')} />
                        <Text style={[styles.iconDropdownOptionText, selected && styles.iconDropdownOptionTextActive, isDark && !selected && { color: colors.text.secondary }]}>
                          {iconName}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : (
                <Text style={[styles.iconDropdownEmptyText, isDark && { color: colors.text.secondary }]}>No icons found</Text>
              )}
            </View>
          ) : null}
        </View>
      </View>

      <View style={[styles.noteWrap, isDark && { borderTopColor: colors.border.divider }]}>
        <Text style={[styles.noteText, isDark && { color: colors.text.secondary }]}>Changes to Report Name will reflect upon next visit to the Reporting tab in Analytics Hub.</Text>
        <Text style={[styles.noteText, isDark && { color: colors.text.secondary }]}>
          Primary and Secondary Tags determine which section reports appear in and what tags are visible within the
          Reports tab of the Analytics Module.
        </Text>
        <Text style={[styles.noteText, isDark && { color: colors.text.secondary }]}>Disabling a report will hide it from the Reports tab in Analytics Hub.</Text>
      </View>
    </AdminModal>
  );
};

export default ReportingConfigurationModal;

const localStyles = StyleSheet.create({
  formInputDisabled: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
    opacity: 0.7,
  },
  disabledFieldHint: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 4,
    fontStyle: 'italic',
  },
});
